import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';

import { PolicyEmployee as Employee, PolicyVersion } from '@app/gen/policy';

import { PolicyDetailVersion } from '../types';

const VERSION_STATUS_TITLES: Record<
  NonNullable<PolicyVersion['version_status']>,
  { title: string; descriptionPlaceholder: string }
> = {
  draft: { title: 'Черновик', descriptionPlaceholder: 'Доступна с' },
  past: { title: 'Прошлая', descriptionPlaceholder: 'Была активна с' },
  actual: { title: 'Актуальная', descriptionPlaceholder: 'Активна с' },
  future: { title: 'Будущая', descriptionPlaceholder: 'Будет активна с' },
};

const NOT_PUBLISHED_DESCRIPTION = 'Не была опубликована';

@injectable()
export class PolicyDetailMapper {
  constructor(private format: FormatService) {}

  processVersions(
    versions: PolicyVersion[],
    id: string,
  ): PolicyDetailVersion[] {
    const now = new Date();

    const sortedVersions = [...versions].sort((first, second) => {
      return new Date(first.active_from) > new Date(second.active_from)
        ? -1
        : 1;
    });

    return sortedVersions.map(
      ({
        policy_version_id,
        active_from,
        active_to,
        accepted_employees,
        total_employees,
        document_date,
        document_number,
        signature_type,
        with_order,
        acceptance_period_days,
        version_status,
        published_at,
      }) => {
        const { title, descriptionPlaceholder } =
          VERSION_STATUS_TITLES[version_status ?? 'future'];

        const isPastUnpublished = version_status === 'past' && !published_at;

        const description = isPastUnpublished
          ? NOT_PUBLISHED_DESCRIPTION
          : `${descriptionPlaceholder} ${this.format.toDate(
              active_from,
            )} ${active_to ? ` по ${this.format.toDate(active_to)}` : ''}${
              acceptance_period_days
                ? `\nСрок ознакомления ${acceptance_period_days} дн.`
                : ''
            }`;

        return {
          id: policy_version_id,
          fileUrl: `/policy/${id}/version/${policy_version_id}/file`,
          title,
          isEditable: !active_to || new Date(active_to) > now,
          description,
          totalEmployees: total_employees,
          acceptedEmployees: accepted_employees || 0,
          active_from,
          active_to,
          document_date,
          document_number,
          acceptance_period_days,
          signatureType: signature_type && {
            value: signature_type,
            label: this.processSignatureType(signature_type),
          },
          withOrder: with_order,
        };
      },
    );
  }

  /**
   * Выбирает версию документа, открываемую на детальной странице по умолчанию:
   * актуальную, при её отсутствии — текущий черновик, иначе — самую раннюю.
   *
   * @param versions - Версии документа.
   * @returns Идентификатор версии, открываемой по умолчанию.
   */
  findActiveVersion(versions: PolicyVersion[]) {
    const activeVersion = versions.find(
      (version) => version.version_status === 'actual',
    );

    if (activeVersion) {
      return activeVersion.policy_version_id;
    }

    const draftVersion = versions.find(
      (version) => version.version_status === 'draft',
    );

    if (draftVersion) {
      return draftVersion.policy_version_id;
    }

    const sortedVersions = [...versions].sort((first, second) => {
      return new Date(first.active_from) > new Date(second.active_from)
        ? 1
        : -1;
    });

    return sortedVersions.length ? sortedVersions[0].policy_version_id : '';
  }

  processSignatureType(
    signatureType: 'pep' | 'unep' | 'unep_disabled' | 'ukep',
  ) {
    switch (signatureType) {
      case 'pep':
        return 'ПЭП';
      case 'unep':
      case 'ukep':
        return 'УНЭП/УКЭП';
      default:
        return signatureType;
    }
  }

  processEmployees(employees: Employee[]) {
    return employees.map((employee) => {
      return {
        ...employee,
        dismissedAt: employee.dismissed_at
          ? this.format.toDate(employee.dismissed_at)
          : '',
      };
    });
  }
}
